import { ContentBox } from "@msuite/ui/app-shell";
import { BlankSlate } from "@msuite/ui/blank-slate";
import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon, icon } from "@msuite/ui/icons";
import { LoadingScreen } from "@msuite/ui/loading-screen";
import { useTranslation } from "@msuite/ui/localization";
import { type TabPanelGroups, Tabs } from "@msuite/ui/tabs";
import React from "react";
import { useParams } from "react-router";
import { Can } from "@/components/citadel";
import { ProjectHeader } from "@/components/projects";
import { useProject } from "@/hooks/use-project";
import { useHiddenProjectTabs } from "@/hooks/use-project-tabs";
import { EditProjectModal } from "@/modals/projects/edit-project-modal";
import { OrganizationSettingsWorksitesAcknowledgementDefinitions } from "@/sites/organization/settings/organization-settings-worksites/organization-settings-worksites-acknowledgement-definitions";
import { ProjectContacts } from "./panels/project-contacts";
import { ProjectDocuments } from "./panels/project-documents";
import { ProjectGallery } from "./panels/project-gallery";
import { ProjectGeneral } from "./panels/project-general";
import { ProjectInvoices } from "./panels/project-invoices";
import { ProjectMeasurements } from "./panels/project-measurement";
import { ProjectOrders } from "./panels/project-orders";
import { ProjectProtocols } from "./panels/project-protocols";
import { ProjectResourceLinks } from "./panels/project-resource-links";
import { ProjectSettings } from "./panels/project-settings";
import { ProjectTimeline } from "./panels/project-timeline";
import { projectTabCyId } from "./tabs";

export function Project() {
	/** Hooks */
	const projectId = useParams().projectId as string;
	const { t } = useTranslation();
	const editProjectModal = useDisclosure();

	const { project, refetch, isError } = useProject({ projectId });
	const hiddenTabs = useHiddenProjectTabs();

	/** Computed */
	const allGroups: TabPanelGroups = [
		{
			name: "general",
			panels: [
				{
					cyId: projectTabCyId("general"),
					label: t("general.general"),
					errors: null,
					panel: () => <ProjectGeneral projectId={projectId} />,
				},
			],
		},
		{
			name: "details",
			panels: [
				{
					cyId: projectTabCyId("timeline"),
					label: t("timeline.timeline"),
					errors: null,
					panel: () => <ProjectTimeline projectId={projectId} />,
				},
				hiddenTabs.includes("invoices")
					? null
					: {
							cyId: projectTabCyId("invoices"),
							label: t("invoices.invoices"),
							errors: null,
							panel: () => <ProjectInvoices projectId={projectId} />,
						},
				{
					cyId: projectTabCyId("contacts"),
					label: t("contacts.contacts"),
					errors: null,
					panel: () => <ProjectContacts projectId={projectId} />,
				},
				{
					cyId: projectTabCyId("documents"),
					label: t("documents.documents"),
					errors: null,
					panel: () => <ProjectDocuments projectId={projectId} />,
				},
				{
					cyId: projectTabCyId("resource-links"),
					label: t("resource_links.resource_links"),
					errors: null,
					panel: () => <ProjectResourceLinks projectId={projectId} />,
				},
				{
					cyId: projectTabCyId("gallery"),
					label: t("gallery.gallery"),
					errors: null,
					panel: () => <ProjectGallery projectId={projectId} />,
				},
				{
					cyId: projectTabCyId("protocol"),
					label: t("protocols.protocols"),
					errors: null,
					panel: () => <ProjectProtocols projectId={projectId} />,
				},
				hiddenTabs.includes("measurements")
					? null
					: {
							cyId: projectTabCyId("measurements"),
							label: t("measurements.measurements"),
							errors: null,
							panel: () => <ProjectMeasurements />,
						},
				{
					cyId: projectTabCyId("acknowledgement-definitions"),
					label: t("organization.worksite_required_acknowledgements"),
					errors: null,
					panel: () => (
						<OrganizationSettingsWorksitesAcknowledgementDefinitions
							worksite="project-site"
							projectId={projectId}
						/>
					),
				},
			],
		},
		{
			name: "stock",
			panels: [
				hiddenTabs.includes("orders")
					? null
					: {
							label: t("stock.orders"),
							cyId: projectTabCyId("orders"),
							errors: null,
							panel: () => <ProjectOrders projectId={projectId} />,
						},
			],
		},
		"spacer",
		{
			name: "admin",
			panels: [
				{
					cyId: projectTabCyId("settings"),
					label: t("settings.settings"),
					errors: null,
					panel: () => <ProjectSettings projectId={projectId} />,
				},
			],
		},
	];

	// A group whose panels are all gated away would still render its empty tab
	// strip, so it goes with them.
	const groups = allGroups.filter((group) => group === "spacer" || group.panels.some(Boolean));

	/** Guard */
	// A refused read never resolves into a project, so without this branch the page spins for ever
	// on a site the caller's reach does not cover — the ordinary answer for a role narrowed to the
	// sites its holder runs, not an exceptional one.
	if (isError) {
		return (
			<ContentBox>
				<BlankSlate
					Icon={icon("Lock")}
					label={t("general.no_access")}
					withAccessory={false}
				/>
			</ContentBox>
		);
	}
	if (!project) return <LoadingScreen />;

	/** Render */
	return (
		<React.Fragment>
			<ContentBox bottomPadding="none">
				<ProjectHeader project={project} />
				<Tabs
					panelKey="project-tabs"
					groups={groups}
					header={() => (
						<Can
							action="update"
							subject="Project"
							resource={{ ...project }}
						>
							<Button
								icon={<Icon name="Edit" />}
								onClick={editProjectModal.onOpen}
								variant="primary"
							>
								{t("projects.edit_project")}
							</Button>
						</Can>
					)}
				/>
			</ContentBox>
			{editProjectModal.isOpen && (
				<EditProjectModal
					isOpen={editProjectModal.isOpen}
					onClose={editProjectModal.onClose}
					projectId={projectId}
					onComplete={refetch}
				/>
			)}
		</React.Fragment>
	);
}
